import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Factory,
  MapPin,
  RefreshCcw,
  ShoppingBag,
  Truck,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  extractRequestedOrderDate,
  formatRequestedOrderDate,
  getLocalYMD,
  getStoreOrderOriginLabel,
  getStoreLogisticsConfig,
  normalizeStoreThemeConfig,
  resolveOrderLogisticsDate,
  resolveProductionOperationalDate,
} from '../utils/storeLogistics';
import {
  buildProductMap,
  buildDailyLogisticsSummary,
  buildSaleItemsBySaleId,
  fetchLogisticsMonthDataset,
  getProductStockForSite,
  getLogisticsMonthRange,
} from '../utils/logisticsPlanning';
import {
  buildProductionRequestLinesFromOrder,
  filterOperationalProductionRequestLines,
  inspectProductionRequestLineDependencies,
  reconcileProductionRequestLinesWithOrders,
} from '../utils/productionOrders';
import { useSite } from '../context/SiteContext';

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
const EMPTY_DAY = { ordersCount: 0, pendingUnits: 0, productionUnits: 0, productsCount: 0 };
const ORDER_STATUS_META = {
  recibido: ['Nuevo', 'bg-blue-100 text-blue-700 border-blue-200'],
  en_preparacion: ['En preparacion', 'bg-amber-100 text-amber-700 border-amber-200'],
  listo: ['Listo', 'bg-violet-100 text-violet-700 border-violet-200'],
  en_camino: ['En camino', 'bg-orange-100 text-orange-700 border-orange-200'],
};

const fmt = (value) =>
  Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const asObject = (value) =>
  value != null && typeof value === 'object' && !Array.isArray(value) ? value : {};

const mergeRowsById = (...collections) => {
  const byId = new Map();

  collections.flat().forEach((row) => {
    if (!row?.id) return;
    byId.set(row.id, row);
  });

  return Array.from(byId.values());
};

const normalizeUuidArray = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean);

const getLinkedRequestLineIds = (row) => {
  if (!row || typeof row !== 'object') return [];

  const directIds = normalizeUuidArray(row.request_line_ids || row.production_request_line_ids);
  if (directIds.length > 0) return directIds;

  const metadataIds = normalizeUuidArray(
    row.metadata?.request_line_ids || row.metadata?.production_request_line_ids,
  );
  if (metadataIds.length > 0) return metadataIds;

  return [];
};

const addDaysToYMD = (value, amount) => {
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  date.setDate(date.getDate() + amount);
  return getLocalYMD(date);
};

const isRangeContained = (outerRange, innerRange) =>
  outerRange.startYMD <= innerRange.startYMD && outerRange.endYMD >= innerRange.endYMD;

const filterDatasetByRange = (dataset, targetRange) => {
  const nextOrders = (dataset?.orders || []).filter((order) => {
    const dateKey = resolveOrderLogisticsDate(order);
    return dateKey >= targetRange.startYMD && dateKey <= targetRange.endYMD;
  });
  const saleIds = new Set(nextOrders.map((order) => order.sale_id).filter(Boolean));
  const nextSaleItems = (dataset?.saleItems || []).filter((item) => saleIds.has(item.sale_id));
  const nextProductions = (dataset?.productions || []).filter((row) => {
    const dateKey = resolveProductionOperationalDate(row);
    return dateKey >= targetRange.startYMD && dateKey <= targetRange.endYMD;
  });
  const productIds = new Set([
    ...nextSaleItems.map((item) => item.product_id).filter(Boolean),
    ...nextProductions.map((row) => row.producto_id).filter(Boolean),
  ]);

  return {
    orders: nextOrders,
    saleItems: nextSaleItems,
    productions: nextProductions,
    products: (dataset?.products || []).filter((product) => productIds.has(product.id)),
  };
};

const buildDateRangeFromYMD = (startYMD, endYMD) => {
  const [startYear, startMonth, startDay] = String(startYMD).split('-').map(Number);
  const [endYear, endMonth, endDay] = String(endYMD).split('-').map(Number);

  return {
    startISO: new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0).toISOString(),
    endISO: new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999).toISOString(),
    startYMD,
    endYMD,
  };
};

const buildProductionSyncSnapshot = (lines) =>
  (lines || [])
    .map((line) => ({
      sourceKey: line.source_key,
      productId: line.product_id || null,
      productName: line.product_name || 'Producto',
      quantity: Number(line.quantity || 0),
      siteId: line.site_id || null,
    }))
    .sort((left, right) => left.sourceKey.localeCompare(right.sourceKey));

const areProductionRequestLinesSynced = ({ expectedLines, currentLines }) => {
  const expected = buildProductionSyncSnapshot(expectedLines);
  const current = buildProductionSyncSnapshot(currentLines);

  if (expected.length !== current.length) return false;

  return expected.every((line, index) => {
    const candidate = current[index];
    return (
      candidate &&
      candidate.sourceKey === line.sourceKey &&
      candidate.productId === line.productId &&
      candidate.productName === line.productName &&
      candidate.siteId === line.siteId &&
      Number(candidate.quantity || 0) === Number(line.quantity || 0)
    );
  });
};

const sumOrderUnits = (orders, itemMapBySaleId) =>
  (orders || []).reduce((acc, order) => {
    const units = (itemMapBySaleId.get(order.sale_id) || []).reduce(
      (itemsAcc, item) => itemsAcc + Number(item.quantity || 0),
      0,
    );
    return acc + units;
  }, 0);

const summarizeProjectionRisk = (days) => {
  const worstShortageByProduct = new Map();
  let firstDate = '';

  (days || []).forEach((day) => {
    (day.shortageRows || []).forEach((row) => {
      const shortageUnits = Math.abs(Number(row.closing || 0));
      const previous = worstShortageByProduct.get(row.key);

      if (!previous || shortageUnits > previous.units) {
        worstShortageByProduct.set(row.key, {
          key: row.key,
          name: row.name,
          units: shortageUnits,
          firstDate: previous?.firstDate || day.dateKey,
        });
      }

      if (!firstDate || day.dateKey < firstDate) firstDate = day.dateKey;
    });
  });

  const rows = Array.from(worstShortageByProduct.values());

  return {
    products: rows.length,
    units: rows.reduce((acc, row) => acc + Number(row.units || 0), 0),
    firstDate,
    preview: rows.slice(0, 2).map((row) => row.name).join(', '),
  };
};

export default function Logistica() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  // ✅ FIX bug #2 multi-sucursal: SiteContext es la fuente única de verdad para el
  // sitio activo. Antes Logistica leía localStorage al montar y NO escuchaba el cambio
  // del Topbar, así que cambiar de sucursal no refrescaba el calendario logístico.
  const { selectedSiteId: contextSiteId, setSelectedSiteId: setContextSiteId } = useSite();
  const selectedSiteId = contextSiteId || '';
  const setSelectedSiteId = (next) => setContextSiteId(next || '');
  const [sitios, setSitios] = useState([]);
  const [storeConfig, setStoreConfig] = useState({ theme_config: normalizeStoreThemeConfig({}) });
  const [orders, setOrders] = useState([]);
  const [saleItems, setSaleItems] = useState([]);
  const [productions, setProductions] = useState([]);
  const [productionRequestLines, setProductionRequestLines] = useState([]);
  const [kpiOrders, setKpiOrders] = useState([]);
  const [kpiSaleItems, setKpiSaleItems] = useState([]);
  const [kpiProductions, setKpiProductions] = useState([]);
  const [kpiProducts, setKpiProducts] = useState([]);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [creatingProductionRequestId, setCreatingProductionRequestId] = useState(null);
  const [updatingProductionDateId, setUpdatingProductionDateId] = useState(null);
  const [dateDrafts, setDateDrafts] = useState({});
  const [productionDateDrafts, setProductionDateDrafts] = useState({});
  const [activeKpi, setActiveKpi] = useState(null);
  const fetchDataRef = useRef(() => {});
  const realtimeRefreshTimeoutRef = useRef(null);

  const range = useMemo(() => getLogisticsMonthRange(currentDate), [currentDate]);
  const logisticsConfig = useMemo(() => getStoreLogisticsConfig(storeConfig), [storeConfig]);
  const todayDateKey = useMemo(() => getLocalYMD(new Date()), []);
  const nextWeekDateKey = useMemo(() => addDaysToYMD(todayDateKey, 7), [todayDateKey]);
  const kpiRange = useMemo(() => buildDateRangeFromYMD(todayDateKey, nextWeekDateKey), [nextWeekDateKey, todayDateKey]);

  useEffect(() => {
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    if (selectedDay > daysInMonth) setSelectedDay(daysInMonth);
  }, [currentDate, selectedDay]);

  // ✅ FIX bug #2: la persistencia ahora la maneja SiteContext (org-scoped). Removido
  // el efecto que escribía a localStorage para evitar duplicación de fuentes.

  useEffect(() => {
    if (!profile?.organization_id) return;
    loadBaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, currentDate, selectedSiteId]);

  useEffect(() => {
    if (!profile?.organization_id) return undefined;

    const organizationId = profile.organization_id;
    const scheduleRefresh = () => {
      if (realtimeRefreshTimeoutRef.current) {
        window.clearTimeout(realtimeRefreshTimeoutRef.current);
      }

      realtimeRefreshTimeoutRef.current = window.setTimeout(() => {
        fetchDataRef.current?.();
      }, 250);
    };

    const channel = supabase
      .channel(`logistica_store_orders_${organizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'store_orders',
          filter: `organization_id=eq.${organizationId}`,
        },
        scheduleRefresh,
      )
      .subscribe();

    return () => {
      if (realtimeRefreshTimeoutRef.current) {
        window.clearTimeout(realtimeRefreshTimeoutRef.current);
        realtimeRefreshTimeoutRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [profile?.organization_id]);

  useEffect(() => {
    if (!activeKpi) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setActiveKpi(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeKpi]);

  const loadBaseData = async () => {
    try {
      const orgId = profile.organization_id;
      const [sitesRes, configRes] = await Promise.all([
        supabase.from('sitios').select('id, nombre').eq('organization_id', orgId).eq('is_active', true).order('nombre'),
        supabase.from('store_config').select('theme_config').eq('organization_id', orgId).maybeSingle(),
      ]);

      if (sitesRes.error) throw sitesRes.error;
      if (configRes.error) throw configRes.error;

      setSitios(sitesRes.data || []);
      setStoreConfig({ theme_config: normalizeStoreThemeConfig(configRes.data?.theme_config) });
    } catch (error) {
      console.error(error);
      toast.error('No se pudo cargar la base de logistica.');
    }
  };

  const fetchData = async () => {
    const firstLoad = loading;
    if (!firstLoad) setRefreshing(true);

    try {
      const orgId = profile.organization_id;
      const canReuseDatasetForKpis = isRangeContained(range, kpiRange);
      const [dataset, kpiDataset] = await Promise.all([
        fetchLogisticsMonthDataset({
          supabase,
          organizationId: orgId,
          range,
          selectedSiteId,
          filterProductionsBySite: false,
        }),
        canReuseDatasetForKpis
          ? Promise.resolve(null)
          : fetchLogisticsMonthDataset({
              supabase,
              organizationId: orgId,
              range: kpiRange,
              selectedSiteId,
            }),
      ]);

      const orderIds = Array.from(new Set((dataset.orders || []).map((order) => order.id).filter(Boolean)));

      let requestLinesByDateQuery = supabase
        .from('production_request_lines')
        .select('*')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .gte('requested_date', range.startYMD)
        .lte('requested_date', range.endYMD)
        .order('requested_date', { ascending: true })
        .order('created_at', { ascending: true });

      if (selectedSiteId) {
        requestLinesByDateQuery = requestLinesByDateQuery.eq('site_id', selectedSiteId);
      }

      let requestLinesByOrderQuery = Promise.resolve({ data: [], error: null });

      if (orderIds.length > 0) {
        requestLinesByOrderQuery = supabase
          .from('production_request_lines')
          .select('*')
          .eq('organization_id', orgId)
          .eq('is_active', true)
          .in('logistics_order_id', orderIds)
          .order('requested_date', { ascending: true })
          .order('created_at', { ascending: true });

        if (selectedSiteId) {
          requestLinesByOrderQuery = requestLinesByOrderQuery.eq('site_id', selectedSiteId);
        }
      }

      const [requestLinesByDateRes, requestLinesByOrderRes] = await Promise.all([
        requestLinesByDateQuery,
        requestLinesByOrderQuery,
      ]);

      if (requestLinesByDateRes.error) throw requestLinesByDateRes.error;
      if (requestLinesByOrderRes.error) throw requestLinesByOrderRes.error;

      const nextRequestLines = filterOperationalProductionRequestLines(
        mergeRowsById(requestLinesByDateRes.data || [], requestLinesByOrderRes.data || []),
      );
      const resolvedKpiDataset = canReuseDatasetForKpis
        ? filterDatasetByRange(dataset, kpiRange)
        : kpiDataset;

      setOrders(dataset.orders);
      setSaleItems(dataset.saleItems);
      setProductions(dataset.productions);
      setProductionRequestLines(nextRequestLines);
      setKpiOrders(resolvedKpiDataset?.orders || []);
      setKpiSaleItems(resolvedKpiDataset?.saleItems || []);
      setKpiProductions(resolvedKpiDataset?.productions || []);
      setKpiProducts(resolvedKpiDataset?.products || []);

      void reconcileProductionRequestLinesWithOrders({
        supabase,
        organizationId: orgId,
        lines: nextRequestLines,
        deactivateClosedLogistics: true,
      })
        .then((reconciledRequestLines) => {
          if (reconciledRequestLines.error) {
            console.error(reconciledRequestLines.error);
            return;
          }

          setProductionRequestLines(
            filterOperationalProductionRequestLines(reconciledRequestLines.data || []),
          );
        })
        .catch((reconcileError) => {
          console.error('Error reconciliando solicitudes logisticas:', reconcileError);
        });
    } catch (error) {
      console.error(error);
      toast.error('No se pudo sincronizar el calendario logistico.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  fetchDataRef.current = fetchData;

  const handleOrderDateChange = async (order, nextDate) => {
    const normalizedDate = String(nextDate || '').trim();
    if (!normalizedDate) return;

    const nextShippingAddress = {
      ...asObject(order.shipping_address),
      requested_date: normalizedDate,
    };
    const nextCustomerSnapshot = {
      ...asObject(order.customer_snapshot),
      requested_date: normalizedDate,
    };

    setDateDrafts((prev) => ({ ...prev, [order.id]: normalizedDate }));
    setUpdatingOrderId(order.id);

    try {
      const { error } = await supabase
        .from('store_orders')
        .update({
          shipping_address: nextShippingAddress,
          customer_snapshot: nextCustomerSnapshot,
        })
        .eq('id', order.id)
        .eq('organization_id', profile.organization_id);

      if (error) throw error;

      const updatedOrder = {
        ...order,
        shipping_address: nextShippingAddress,
        customer_snapshot: nextCustomerSnapshot,
      };
      const updatedDate = resolveOrderLogisticsDate(updatedOrder);
      const updatedDay = Number(updatedDate.slice(8, 10));
      const insideCurrentMonth = updatedDate >= range.startYMD && updatedDate <= range.endYMD;

      setOrders((prev) =>
        prev.flatMap((row) => {
          if (row.id !== order.id) return [row];
          return insideCurrentMonth ? [updatedOrder] : [];
        }),
      );

      if (insideCurrentMonth) {
        setSelectedDay(updatedDay);
      }

      setDateDrafts((prev) => {
        const nextDrafts = { ...prev };
        delete nextDrafts[order.id];
        return nextDrafts;
      });

      toast.success('Dia del pedido actualizado.');
    } catch (error) {
      console.error(error);
      toast.error('No se pudo actualizar el dia del pedido.');
      setDateDrafts((prev) => {
        const nextDrafts = { ...prev };
        delete nextDrafts[order.id];
        return nextDrafts;
      });
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleCreateProductionRequest = async (order) => {
    const orderItems = itemMapBySaleId.get(order.sale_id) || [];
    const requestedDate =
      String(productionDateDrafts[order.id] || productionRequestInfoByOrderId.get(order.id)?.requestedDate || todayDateKey).trim() ||
      todayDateKey;
    const lines = buildProductionRequestLinesFromOrder({
      organizationId: profile.organization_id,
      order,
      orderItems,
      requestedDate,
    });

    if (lines.length === 0) {
      toast.error('El pedido no tiene productos operativos para enviar a produccion.');
      return;
    }

    setCreatingProductionRequestId(order.id);

    try {
      const currentLines = productionRequestInfoByOrderId.get(order.id)?.lines || [];
      const nextSourceKeys = new Set(lines.map((line) => line.source_key));
      const staleLineIds = currentLines
        .filter((line) => !nextSourceKeys.has(line.source_key))
        .map((line) => line.id)
        .filter(Boolean);
      const willUpdateExistingLines =
        currentLines.length > 0 &&
        !areProductionRequestLinesSynced({
          expectedLines: lines,
          currentLines,
        });

      if (willUpdateExistingLines) {
        const dependenciesRes = await inspectProductionRequestLineDependencies({
          supabase,
          organizationId: profile.organization_id,
          lines: currentLines,
        });

        if (dependenciesRes.error) throw dependenciesRes.error;
        if (dependenciesRes.data?.hasBlockedLines) {
          toast.error('No puedes actualizar este envio porque ya tiene produccion o avances cargados.');
          return;
        }
      }

      const { data, error } = await supabase
        .from('production_request_lines')
        .upsert(lines, {
          onConflict: 'organization_id,source_key',
        })
        .select();

      if (error) throw error;
      if (staleLineIds.length > 0) {
        const { error: staleError } = await supabase
          .from('production_request_lines')
          .update({ is_active: false })
          .eq('organization_id', profile.organization_id)
          .in('id', staleLineIds);

        if (staleError) throw staleError;
      }

      setProductionDateDrafts((prev) => ({ ...prev, [order.id]: requestedDate }));
      setProductionRequestLines((prev) =>
        filterOperationalProductionRequestLines(
          mergeRowsById(prev.filter((row) => !staleLineIds.includes(row.id)), data || []),
        ),
      );
      toast.success(currentLines.length > 0 ? 'Pedido de produccion actualizado.' : 'Pedido enviado a produccion.');
    } catch (error) {
      console.error(error);
      toast.error('No se pudo crear el pedido de produccion.');
    } finally {
      setCreatingProductionRequestId(null);
    }
  };

  const handleProductionRequestDateChange = async (order, nextDate) => {
    const normalizedDate = String(nextDate || '').trim();
    if (!normalizedDate) return;

    const requestInfo = productionRequestInfoByOrderId.get(order.id);
    const currentRequestedDate = requestInfo?.requestedDate || todayDateKey;
    if (normalizedDate === currentRequestedDate) {
      setProductionDateDrafts((prev) => ({ ...prev, [order.id]: normalizedDate }));
      return;
    }

    setProductionDateDrafts((prev) => ({ ...prev, [order.id]: normalizedDate }));
    if (!requestInfo?.lines?.length) return;

    setUpdatingProductionDateId(order.id);

    try {
      const dependenciesRes = await inspectProductionRequestLineDependencies({
        supabase,
        organizationId: profile.organization_id,
        lines: requestInfo.lines,
      });

      if (dependenciesRes.error) throw dependenciesRes.error;
      if (dependenciesRes.data?.hasBlockedLines) {
        throw new Error('No puedes mover la fecha porque el pedido ya tiene produccion o avances cargados.');
      }

      const { data, error } = await supabase
        .from('production_request_lines')
        .update({ requested_date: normalizedDate })
        .eq('organization_id', profile.organization_id)
        .eq('logistics_order_id', order.id)
        .eq('is_active', true)
        .select();

      if (error) throw error;

      const updatedIds = new Set((data || []).map((row) => row.id));
      setProductionRequestLines((prev) =>
        filterOperationalProductionRequestLines(
          mergeRowsById(prev.filter((row) => !updatedIds.has(row.id)), data || []),
        ),
      );
      toast.success('Fecha de produccion actualizada.');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'No se pudo actualizar la fecha de produccion.');
      setProductionDateDrafts((prev) => ({ ...prev, [order.id]: currentRequestedDate }));
    } finally {
      setUpdatingProductionDateId(null);
    }
  };

  const itemMapBySaleId = useMemo(() => buildSaleItemsBySaleId(saleItems), [saleItems]);
  const kpiItemMapBySaleId = useMemo(() => buildSaleItemsBySaleId(kpiSaleItems), [kpiSaleItems]);
  const kpiProductMap = useMemo(() => buildProductMap(kpiProducts), [kpiProducts]);
  const siteNameById = useMemo(() => new Map((sitios || []).map((site) => [site.id, site.nombre])), [sitios]);

  const dailySummary = useMemo(
    () => buildDailyLogisticsSummary({ orders, productions, itemMapBySaleId, productionRequests: productionRequestLines }),
    [itemMapBySaleId, orders, productionRequestLines, productions],
  );

  const selectedDateKey = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDay).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [currentDate, selectedDay]);

  const selectedDayOrders = useMemo(
    () => orders.filter((order) => resolveOrderLogisticsDate(order) === selectedDateKey),
    [orders, selectedDateKey],
  );

  const productionRequestInfoByOrderId = useMemo(() => {
    const grouped = new Map();

    productionRequestLines.forEach((line) => {
      if (!line.logistics_order_id) return;

      const previous = grouped.get(line.logistics_order_id) || {
        totalUnits: 0,
        lineCount: 0,
        requestedDate: '',
        lines: [],
      };

      const requestedDate = String(line.requested_date || '').trim();
      previous.totalUnits += Number(line.quantity || 0);
      previous.lineCount += 1;
      previous.lines.push(line);
      if (!previous.requestedDate || (requestedDate && requestedDate < previous.requestedDate)) {
        previous.requestedDate = requestedDate;
      }

      grouped.set(line.logistics_order_id, previous);
    });

    return grouped;
  }, [productionRequestLines]);

  const productionRequestStateByOrderId = useMemo(() => {
    const states = new Map();

    orders.forEach((order) => {
      const existingLines = productionRequestInfoByOrderId.get(order.id)?.lines || [];
      const requestedDate = productionRequestInfoByOrderId.get(order.id)?.requestedDate || todayDateKey;
      const orderItems = itemMapBySaleId.get(order.sale_id) || [];
      const expectedLines = buildProductionRequestLinesFromOrder({
        organizationId: profile?.organization_id || '',
        order,
        orderItems,
        requestedDate,
      });
      const isSent = existingLines.length > 0;
      const isSynced = isSent ? areProductionRequestLinesSynced({ expectedLines, currentLines: existingLines }) : false;

      states.set(order.id, {
        isSent,
        isSynced,
      });
    });

    return states;
  }, [itemMapBySaleId, orders, productionRequestInfoByOrderId, profile?.organization_id, todayDateKey]);

  useEffect(() => {
    setProductionDateDrafts((current) => {
      const nextDrafts = { ...current };
      const activeOrderIds = new Set();
      let changed = false;

      orders.forEach((order) => {
        activeOrderIds.add(order.id);
        const requestedDate = productionRequestInfoByOrderId.get(order.id)?.requestedDate || current[order.id] || todayDateKey;
        if (nextDrafts[order.id] !== requestedDate) {
          nextDrafts[order.id] = requestedDate;
          changed = true;
        }
      });

      Object.keys(nextDrafts).forEach((orderId) => {
        if (!activeOrderIds.has(orderId)) {
          delete nextDrafts[orderId];
          changed = true;
        }
      });

      return changed ? nextDrafts : current;
    });
  }, [orders, productionRequestInfoByOrderId, todayDateKey]);

  const selectedDayProductions = useMemo(
    () => productions.filter((row) => resolveProductionOperationalDate(row) === selectedDateKey),
    [productions, selectedDateKey],
  );

  const selectedDayProductionRequests = useMemo(
    () =>
      productionRequestLines.filter((line) => String(line.requested_date || '').trim() === selectedDateKey),
    [productionRequestLines, selectedDateKey],
  );

  const todayOrders = useMemo(
    () => kpiOrders.filter((order) => resolveOrderLogisticsDate(order) === todayDateKey),
    [kpiOrders, todayDateKey],
  );

  const todayProductions = useMemo(
    () => kpiProductions.filter((row) => resolveProductionOperationalDate(row) === todayDateKey),
    [kpiProductions, todayDateKey],
  );

  const todayOrdersUnits = useMemo(
    () => sumOrderUnits(todayOrders, kpiItemMapBySaleId),
    [kpiItemMapBySaleId, todayOrders],
  );

  const todayProductionUnits = useMemo(
    () => todayProductions.reduce((acc, row) => acc + Number(row.cantidad || 0), 0),
    [todayProductions],
  );

  const stockProjection = useMemo(() => {
    const productMetaByKey = new Map();
    const pendingByDateAndProduct = new Map();
    const producedByDateAndProduct = new Map();
    const keys = new Set();

    const ensureProductMeta = (key, productId, name) => {
      if (!key) return;

      const previous = productMetaByKey.get(key) || {};
      productMetaByKey.set(key, {
        productId: previous.productId || productId || null,
        name: previous.name || name || 'Producto',
      });
      keys.add(key);
    };

    kpiOrders.forEach((order) => {
      const dateKey = resolveOrderLogisticsDate(order);
      if (dateKey < todayDateKey || dateKey > nextWeekDateKey) return;

      (kpiItemMapBySaleId.get(order.sale_id) || []).forEach((item) => {
        const key = item.product_id || item.product_name_snapshot;
        if (!key) return;

        ensureProductMeta(key, item.product_id || null, item.product_name_snapshot || 'Producto');

        const mapKey = `${dateKey}::${key}`;
        pendingByDateAndProduct.set(mapKey, Number(pendingByDateAndProduct.get(mapKey) || 0) + Number(item.quantity || 0));
      });
    });

    kpiProductions.forEach((row) => {
      const dateKey = resolveProductionOperationalDate(row);
      if (dateKey < todayDateKey || dateKey > nextWeekDateKey) return;

      const key = row.producto_id || row.producto_nombre;
      if (!key) return;

      ensureProductMeta(key, row.producto_id || null, row.producto_nombre || 'Producto');

      const mapKey = `${dateKey}::${key}`;
      producedByDateAndProduct.set(mapKey, Number(producedByDateAndProduct.get(mapKey) || 0) + Number(row.cantidad || 0));
    });

    const runningStockByKey = new Map();

    Array.from(keys).forEach((key) => {
      const meta = productMetaByKey.get(key) || {};
      const product = meta.productId ? kpiProductMap.get(meta.productId) : null;
      runningStockByKey.set(key, getProductStockForSite(product, selectedSiteId));
    });

    return Array.from({ length: 8 }, (_, index) => {
      const dateKey = addDaysToYMD(todayDateKey, index);
      const rows = Array.from(keys)
        .map((key) => {
          const opening = Number(runningStockByKey.get(key) || 0);
          const produced = Number(producedByDateAndProduct.get(`${dateKey}::${key}`) || 0);
          const pending = Number(pendingByDateAndProduct.get(`${dateKey}::${key}`) || 0);
          const closing = opening + produced - pending;
          const meta = productMetaByKey.get(key) || {};

          runningStockByKey.set(key, closing);

          return {
            key,
            name: meta.name || 'Producto',
            opening,
            produced,
            pending,
            closing,
          };
        })
        .filter((row) => row.opening !== 0 || row.produced !== 0 || row.pending !== 0 || row.closing !== 0);

      return {
        dateKey,
        rows,
        shortageRows: rows.filter((row) => row.closing < 0),
      };
    });
  }, [kpiItemMapBySaleId, kpiOrders, kpiProductions, kpiProductMap, nextWeekDateKey, selectedSiteId, todayDateKey]);

  const todayRisk = useMemo(() => summarizeProjectionRisk(stockProjection.slice(0, 1)), [stockProjection]);
  const nextWeekRisk = useMemo(() => summarizeProjectionRisk(stockProjection.slice(1)), [stockProjection]);
  const todayRiskLabel = todayRisk.products > 0 ? `${fmt(todayRisk.units)}u en riesgo hoy` : 'Sin faltantes para hoy';
  const nextWeekRiskLabel = nextWeekRisk.products > 0
    ? `${fmt(nextWeekRisk.units)}u en riesgo${nextWeekRisk.firstDate ? ` desde ${formatRequestedOrderDate(nextWeekRisk.firstDate)}` : ''}`
    : `Sin faltantes hasta ${formatRequestedOrderDate(nextWeekDateKey)}`;
  const selectedSiteName = selectedSiteId ? siteNameById.get(selectedSiteId) || 'Sitio' : 'Consolidado';

  const selectedDayProductionRows = useMemo(() => {
    const grouped = new Map();
    const requestLineById = new Map(
      (selectedDayProductionRequests || [])
        .filter((line) => line?.id)
        .map((line) => [line.id, line]),
    );

    const ensureRow = (key, name) => {
      const previous = grouped.get(key) || {
        key,
        name: name || 'Producto',
        requested: 0,
        produced: 0,
        requestsCount: 0,
        recordsCount: 0,
        siteIds: new Set(),
      };

      grouped.set(key, previous);
      return previous;
    };

    selectedDayProductionRequests.forEach((line) => {
      const key = line.product_id || line.product_name || line.source_key;
      const row = ensureRow(key, line.product_name || 'Producto');
      row.requested += Number(line.quantity || 0);
      row.requestsCount += 1;
      if (line.site_id) row.siteIds.add(line.site_id);
    });

    selectedDayProductions.forEach((row) => {
      const linkedLines = getLinkedRequestLineIds(row)
        .map((lineId) => requestLineById.get(lineId))
        .filter(Boolean);

      if (linkedLines.length > 0) {
        const consumedKeys = new Set();

        linkedLines.forEach((line) => {
          const key = line.product_id || line.product_name || row.producto_id || row.producto_nombre || row.id;
          if (!key || consumedKeys.has(key)) return;
          consumedKeys.add(key);

          const productionRow = ensureRow(key, line.product_name || row.producto_nombre || 'Producto');
          productionRow.produced += Number(row.cantidad || 0);
          productionRow.recordsCount += 1;
          if (line.site_id) productionRow.siteIds.add(line.site_id);
        });

        return;
      }

      const key = row.producto_id || row.producto_nombre || row.id;
      const productionRow = ensureRow(key, row.producto_nombre || 'Producto');
      productionRow.produced += Number(row.cantidad || 0);
      productionRow.recordsCount += 1;
      if (row.sitio_id) productionRow.siteIds.add(row.sitio_id);
    });

    return Array.from(grouped.values())
      .map((row) => ({
        ...row,
        pending: Math.max(row.requested - row.produced, 0),
        siteLabel:
          row.siteIds.size === 1
            ? siteNameById.get(Array.from(row.siteIds)[0]) || selectedSiteName
            : selectedSiteName,
      }))
      .sort((left, right) => {
        if (right.pending !== left.pending) return right.pending - left.pending;
        if (right.requested !== left.requested) return right.requested - left.requested;
        return left.name.localeCompare(right.name, 'es', { sensitivity: 'base' });
      });
  }, [selectedDayProductionRequests, selectedDayProductions, selectedSiteName, siteNameById]);

  const selectedDayProductionTotals = useMemo(
    () =>
      selectedDayProductionRows.reduce(
        (acc, row) => ({
          requested: acc.requested + Number(row.requested || 0),
          produced: acc.produced + Number(row.produced || 0),
          pending: acc.pending + Number(row.pending || 0),
        }),
        { requested: 0, produced: 0, pending: 0 },
      ),
    [selectedDayProductionRows],
  );

  const todayOrderDetails = useMemo(
    () =>
      todayOrders.map((order) => {
        const orderItems = kpiItemMapBySaleId.get(order.sale_id) || [];
        const units = orderItems.reduce((acc, item) => acc + Number(item.quantity || 0), 0);

        return {
          id: order.id,
          title: `#${order.tracking_code || order.id.slice(0, 8)}`,
          subtitle: order.customer_snapshot?.name || order.customer_snapshot?.contact || 'Cliente web',
          badge: `${fmt(units)}u`,
          meta: formatRequestedOrderDate(resolveOrderLogisticsDate(order)),
          chips: orderItems.map((item, index) => ({
            key: `${order.id}-${item.product_id || index}`,
            label: `${fmt(item.quantity)} x ${item.product_name_snapshot || 'Producto'}`,
          })),
        };
      }),
    [kpiItemMapBySaleId, todayOrders],
  );

  const todayProductionDetails = useMemo(
    () =>
      todayProductions.map((row) => ({
        id: row.id,
        title: row.producto_nombre || 'Producto',
        subtitle: row.sitios?.nombre || selectedSiteName,
        badge: `${fmt(row.cantidad)}u`,
        meta: formatRequestedOrderDate(resolveProductionOperationalDate(row)),
      })),
    [selectedSiteName, todayProductions],
  );

  const todayRiskDetails = useMemo(
    () =>
      (stockProjection[0]?.shortageRows || [])
        .map((row) => ({
          id: `today-${row.key}`,
          title: row.name,
          subtitle: `Faltan ${fmt(Math.abs(row.closing))}u para cubrir hoy`,
          badge: formatRequestedOrderDate(todayDateKey),
          meta: `Stock ${fmt(row.opening)} - Produccion ${fmt(row.produced)} - Pedidos ${fmt(row.pending)}`,
          shortage: Math.abs(Number(row.closing || 0)),
        }))
        .sort((left, right) => right.shortage - left.shortage),
    [stockProjection, todayDateKey],
  );

  const nextWeekRiskDetails = useMemo(() => {
    const byProduct = new Map();

    stockProjection.slice(1).forEach((day) => {
      (day.shortageRows || []).forEach((row) => {
        const shortage = Math.abs(Number(row.closing || 0));
        const previous = byProduct.get(row.key);

        if (!previous) {
          byProduct.set(row.key, {
            id: `future-${row.key}`,
            title: row.name,
            subtitle: `Primera alerta ${formatRequestedOrderDate(day.dateKey)}`,
            badge: `${fmt(shortage)}u`,
            meta: `Peor cierre ${fmt(row.closing)} - ${formatRequestedOrderDate(day.dateKey)}`,
            shortage,
            firstDate: day.dateKey,
            worstClosing: row.closing,
            worstDate: day.dateKey,
          });
          return;
        }

        if (day.dateKey < previous.firstDate) {
          previous.firstDate = day.dateKey;
          previous.subtitle = `Primera alerta ${formatRequestedOrderDate(day.dateKey)}`;
        }

        if (shortage > previous.shortage) {
          previous.shortage = shortage;
          previous.badge = `${fmt(shortage)}u`;
          previous.worstClosing = row.closing;
          previous.worstDate = day.dateKey;
          previous.meta = `Peor cierre ${fmt(row.closing)} - ${formatRequestedOrderDate(day.dateKey)}`;
        }
      });
    });

    return Array.from(byProduct.values()).sort((left, right) => right.shortage - left.shortage);
  }, [stockProjection]);

  const kpiDetailMap = useMemo(
    () => ({
      todayOrders: {
        title: 'Pedidos del dia',
        subtitle: `${todayOrders.length} pedido(s) programado(s) para hoy`,
        items: todayOrderDetails,
        empty: 'No hay pedidos programados para hoy.',
      },
      todayProduction: {
        title: 'Produccion del dia',
        subtitle: `${todayProductions.length} registro(s) de produccion cargados para hoy`,
        items: todayProductionDetails,
        empty: 'No hay produccion cargada para hoy.',
      },
      todayRisk: {
        title: 'Alerta stock hoy',
        subtitle: todayRisk.products > 0 ? `${todayRisk.products} producto(s) en riesgo hoy` : 'Hoy no hay faltantes proyectados.',
        items: todayRiskDetails,
        empty: 'No hay productos en alerta para hoy.',
      },
      nextWeekRisk: {
        title: 'Alerta proximos 7 dias',
        subtitle: nextWeekRisk.products > 0 ? `${nextWeekRisk.products} producto(s) con riesgo en la proxima semana` : 'No hay faltantes proyectados para la proxima semana.',
        items: nextWeekRiskDetails,
        empty: 'No hay productos en alerta para los proximos 7 dias.',
      },
    }),
    [
      nextWeekRisk.products,
      nextWeekRiskDetails,
      todayOrderDetails,
      todayOrders.length,
      todayProductions.length,
      todayProductionDetails,
      todayRisk.products,
      todayRiskDetails,
    ],
  );

  const activeKpiDetail = activeKpi ? kpiDetailMap[activeKpi] : null;

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const startIndex = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const calendarDays = Array(startIndex).fill(null).concat([...Array(daysInMonth).keys()].map((index) => index + 1));
  const monthLabel = currentDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
  const selectedDateLabel = new Date(`${selectedDateKey}T12:00:00`).toLocaleDateString('es-AR', { weekday: 'long', day: '2-digit', month: 'long' });

  const moveMonth = (step) => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + step, 1));
    setSelectedDay(1);
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={36} />
        <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Cargando logistica...</p>
      </div>
    );
  }

  return (
    <div className="imp-page imp-container animate-nova-in">
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Truck size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Logistica</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Calendario operativo de pedidos web y produccion.</p>
          </div>
        </div>

        <div className="imp-header-actions">
          <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <MapPin size={14} className="text-slate-400" />
            <select value={selectedSiteId} onChange={(event) => setSelectedSiteId(event.target.value)} className="bg-transparent text-[10px] font-black uppercase tracking-widest text-slate-600 outline-none cursor-pointer">
              <option value="">Consolidado</option>
              {sitios.map((site) => <option key={site.id} value={site.id}>{site.nombre}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-1 px-1 py-1 rounded-2xl bg-[#0B2A36] text-white shadow-lg">
            <button type="button" onClick={() => moveMonth(-1)} className="p-2 rounded-xl hover:bg-white/10 transition-all"><ChevronLeft size={16} /></button>
            <div className="px-3 text-[10px] font-black uppercase tracking-widest">{monthLabel}</div>
            <button type="button" onClick={() => moveMonth(1)} className="p-2 rounded-xl hover:bg-white/10 transition-all"><ChevronRight size={16} /></button>
          </div>

          <button type="button" onClick={fetchData} disabled={refreshing} className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60">
            <RefreshCcw size={14} className={refreshing ? 'animate-spin' : ''} /> Actualizar
          </button>

        </div>
      </div>

      <div className="imp-kpis mb-3">
        <button type="button" onClick={() => setActiveKpi('todayOrders')} className="imp-kpi text-left transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/40">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pedidos del dia</p>
            <ShoppingBag size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{todayOrders.length}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{fmt(todayOrdersUnits)} unidades programadas</p>
        </button>

        <button type="button" onClick={() => setActiveKpi('todayProduction')} className="imp-kpi text-left transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500/30">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Produccion del dia</p>
            <Factory size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{fmt(todayProductionUnits)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{todayProductions.length} carga(s) productivas</p>
        </button>

        <button type="button" onClick={() => setActiveKpi('todayRisk')} className="imp-kpi text-left transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-rose-500/30">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Alerta stock hoy</p>
            <AlertTriangle size={16} className={todayRisk.products > 0 ? 'text-rose-500' : 'text-emerald-500'} />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${todayRisk.products > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{todayRisk.products}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{todayRiskLabel}</p>
        </button>

        <button type="button" onClick={() => setActiveKpi('nextWeekRisk')} className="imp-kpi text-left transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-amber-500/30">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Alerta proximos 7 dias</p>
            <CalendarDays size={16} className={nextWeekRisk.products > 0 ? 'text-amber-500' : 'text-emerald-500'} />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${nextWeekRisk.products > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
            {nextWeekRisk.products}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{nextWeekRiskLabel}</p>
        </button>
      </div>

      {!logisticsConfig.request_date_enabled ? (
        <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[11px] text-amber-800">
          La fecha operativa en pedidos web esta desactivada. El calendario usa la fecha de ingreso del pedido como respaldo.
        </div>
      ) : null}

      <div className="space-y-3">
        <div className="space-y-3">
          <div className="grid grid-cols-7 gap-2">
            {DAY_NAMES.map((dayName) => (
              <div key={dayName} className="text-center text-[9px] font-black text-slate-400 uppercase tracking-widest pb-1">
                {dayName}
              </div>
            ))}

            {calendarDays.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="h-24 md:h-28 rounded-2xl border border-dashed border-slate-100 opacity-20" />;
              }

              const info = dailySummary[day] || EMPTY_DAY;
              const selected = selectedDay === day;
              const hasActivity = info.ordersCount > 0 || info.productionUnits > 0;

              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedDay(day)}
                  className={`h-24 md:h-28 p-2 rounded-xl border transition-all text-left flex flex-col justify-between ${
                    selected
                      ? 'bg-[#0B2A36] border-[#0B2A36] text-white shadow-lg scale-[1.02] z-10'
                      : 'bg-white border-slate-100 hover:border-[#51A7FD] shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className={`text-sm md:text-base font-black ${selected ? 'text-white' : 'text-slate-500'}`}>{day}</span>
                    {hasActivity ? (
                      <span className={`h-2.5 w-2.5 rounded-full ${info.pendingUnits > info.productionUnits ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                    ) : null}
                  </div>

                  <div className="space-y-1 text-right">
                    <div>
                      <p className={`text-[6px] font-black uppercase tracking-tighter ${selected ? 'text-slate-400' : 'text-slate-300'}`}>Pedidos</p>
                      <p className={`text-[9px] md:text-[10px] font-black tracking-tight ${selected ? 'text-[#3FF2AA]' : 'text-[#51A7FD]'}`}>
                        {info.ordersCount} / {fmt(info.pendingUnits)}u
                      </p>
                    </div>
                    <div>
                      <p className={`text-[6px] font-black uppercase tracking-tighter ${selected ? 'text-slate-400' : 'text-slate-300'}`}>Prod.</p>
                      <p className={`text-[9px] md:text-[10px] font-black tracking-tight ${selected ? 'text-white' : 'text-emerald-600'}`}>
                        {fmt(info.productionUnits)}u
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Detalle del dia</p>
                <h3 className="text-sm font-black uppercase tracking-tight text-slate-800 mt-1">{selectedDateLabel}</h3>
              </div>
              <div className="text-right">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Sitio</p>
                <p className="text-[11px] font-black uppercase tracking-tight text-slate-700">{selectedSiteName}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
              <div className="rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-[#0B2A36] text-white flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                    <ShoppingBag size={14} className="text-[#3FF2AA]" />
                    Pedidos
                  </span>
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{selectedDayOrders.length}</span>
                </div>

                <div className="p-3 space-y-2 max-h-[340px] overflow-y-auto custom-scrollbar bg-slate-50">
                  {selectedDayOrders.length > 0 ? (
                    selectedDayOrders.map((order) => {
                      const meta = ORDER_STATUS_META[order.status] || ['Pendiente', 'bg-slate-100 text-slate-700 border-slate-200'];
                      const orderItems = itemMapBySaleId.get(order.sale_id) || [];
                      const units = orderItems.reduce((acc, item) => acc + Number(item.quantity || 0), 0);
                      const effectiveDate = resolveOrderLogisticsDate(order);
                      const editableDate = dateDrafts[order.id] || extractRequestedOrderDate(order) || effectiveDate;
                      const productionRequestInfo = productionRequestInfoByOrderId.get(order.id) || null;
                      const productionRequestState = productionRequestStateByOrderId.get(order.id) || { isSent: false, isSynced: false };
                      const productionDate = productionDateDrafts[order.id] || productionRequestInfo?.requestedDate || todayDateKey;
                      const isSentToProduction = Boolean(productionRequestState.isSent);
                      const isProductionRequestSynced = Boolean(productionRequestState.isSynced);
                      const isUpdatingDate = updatingOrderId === order.id;
                      const isUpdatingProductionDate = updatingProductionDateId === order.id;
                      const sourceLabel = getStoreOrderOriginLabel(order);
                      const tooltipText =
                        logisticsConfig.request_date_helper || 'Define el dia operativo del pedido desde el calendario logistico.';

                      return (
                        <div key={order.id} className="rounded-[18px] border border-slate-200 bg-[linear-gradient(135deg,_#ffffff_0%,_#f8fbff_100%)] p-3 shadow-sm">
                          <div className="flex items-start justify-between gap-2.5">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-[10px] font-black uppercase tracking-wider text-slate-800 truncate">
                                  #{order.tracking_code || order.id.slice(0, 8)}
                                </p>
                                <span className="px-2 py-1 rounded-lg bg-slate-100 text-[8px] font-black uppercase tracking-widest text-slate-600">
                                  {sourceLabel}
                                </span>
                              </div>
                              <p className="text-[10px] font-bold text-slate-500 truncate">
                                {order.customer_snapshot?.name || order.customer_snapshot?.contact || 'Cliente web'}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest ${meta[1]}`}>{meta[0]}</span>
                          </div>

                          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <div className="rounded-2xl bg-white border border-slate-200 px-3 py-2.5 shadow-sm">
                              <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Para</p>
                              <p className="mt-1 text-[10px] font-black text-slate-700">{formatRequestedOrderDate(effectiveDate)}</p>
                            </div>
                            <div className="rounded-2xl bg-white border border-slate-200 px-3 py-2.5 shadow-sm">
                              <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Total pedido</p>
                              <p className="mt-1 text-[10px] font-black text-slate-700">{fmt(units)} unidades</p>
                            </div>
                            <div className="rounded-2xl bg-white border border-slate-200 px-3 py-2.5 shadow-sm">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Produccion para</p>
                                {isUpdatingProductionDate ? (
                                  <span className="inline-flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-emerald-600">
                                    <RefreshCcw size={10} className="animate-spin" />
                                    Guardando
                                  </span>
                                ) : null}
                              </div>
                              <input
                                type="date"
                                value={productionDate}
                                disabled={creatingProductionRequestId === order.id || isUpdatingProductionDate}
                                onChange={(event) => handleProductionRequestDateChange(order, event.target.value)}
                                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2 text-[10px] font-black text-slate-700 outline-none focus:border-emerald-400 disabled:opacity-60"
                              />
                            </div>
                          </div>

                          <div className="mt-2.5 rounded-[18px] border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                                  Productos pedidos
                                </p>
                                <p className="mt-1 text-[9px] font-bold text-slate-500">
                                  {orderItems.length} producto(s) en la orden
                                </p>
                              </div>
                              <div className="rounded-full bg-[#0B2A36] px-2.5 py-1 text-[8px] font-black uppercase tracking-widest text-white">
                                {fmt(units)}u
                              </div>
                            </div>

                            <div className="mt-2.5 flex flex-wrap gap-1.5">
                              {orderItems.map((item, index) => (
                                <div key={`${order.id}-item-${item.product_id || index}`} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[9px] font-bold text-slate-600">
                                  {fmt(item.quantity)} x {item.product_name_snapshot || 'Producto'}
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="mt-2.5 rounded-[18px] border border-slate-200 bg-slate-50 px-3 py-2.5">
                            <div className="flex items-center justify-between gap-3">
                              <div className="relative group inline-flex items-center">
                                <label className="cursor-help text-[8px] font-black uppercase tracking-widest text-slate-400">
                                  {logisticsConfig.request_date_label || 'Dia del pedido'}
                                </label>
                                {logisticsConfig.request_date_enabled ? (
                                  <div className="pointer-events-none absolute left-1/2 top-[calc(100%+8px)] z-20 w-56 -translate-x-1/2 rounded-2xl bg-[#0B2A36] px-3 py-2 text-[10px] font-bold leading-relaxed text-white opacity-0 shadow-xl transition-all duration-150 group-hover:opacity-100">
                                    {tooltipText}
                                  </div>
                                ) : null}
                              </div>
                              {isUpdatingDate ? (
                                <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-sky-600">
                                  <RefreshCcw size={11} className="animate-spin" />
                                  Guardando
                                </span>
                              ) : null}
                            </div>

                            {logisticsConfig.request_date_enabled ? (
                              <>
                                <input
                                  type="date"
                                  value={editableDate || ''}
                                  disabled={isUpdatingDate}
                                  onChange={(event) => handleOrderDateChange(order, event.target.value)}
                                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold text-slate-700 outline-none focus:border-[#51A7FD] disabled:opacity-60"
                                />
                              </>
                            ) : (
                              <p className="mt-2 text-[10px] font-bold text-slate-500">
                                Se usa la fecha de ingreso del pedido. Activa este campo en Config. Logistica para reprogramarlo.
                              </p>
                            )}
                          </div>

                          <div className="mt-2.5 flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleCreateProductionRequest(order)}
                              disabled={creatingProductionRequestId === order.id || (isSentToProduction && isProductionRequestSynced)}
                              className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-60 ${
                                isSentToProduction && isProductionRequestSynced
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : isSentToProduction
                                    ? 'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700'
                                  : 'bg-[#3FF2AA] text-[#0B2A36] shadow-sm hover:brightness-95'
                              }`}
                            >
                              {creatingProductionRequestId === order.id
                                ? 'Enviando...'
                                : isSentToProduction && isProductionRequestSynced
                                  ? 'Enviado a produccion'
                                  : isSentToProduction
                                    ? 'Actualizar envio'
                                  : 'Enviar a produccion'}
                            </button>
                            <button
                              type="button"
                              onClick={() => navigate(`/sistema/pedido-web-detalle/${order.id}`)}
                              className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-600 hover:border-[#51A7FD] hover:text-[#0B2A36] transition-all"
                            >
                              Ver pedido
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="h-[220px] flex flex-col items-center justify-center text-center text-slate-300">
                      <ShoppingBag size={26} />
                      <p className="mt-2 text-[10px] font-black uppercase tracking-widest">Sin pedidos para este dia</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-white border-b border-slate-100 flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-700 flex items-center gap-2">
                    <Factory size={14} className="text-emerald-500" />
                    Produccion
                  </span>
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{selectedDayProductionRows.length}</span>
                </div>

                <div className="p-3 space-y-2 max-h-[340px] overflow-y-auto custom-scrollbar bg-slate-50">
                  {selectedDayProductionRows.length > 0 ? (
                    <>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Enviado</p>
                          <p className="mt-1 text-sm font-black text-slate-800">{fmt(selectedDayProductionTotals.requested)}u</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Producido</p>
                          <p className="mt-1 text-sm font-black text-emerald-700">{fmt(selectedDayProductionTotals.produced)}u</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Pendiente</p>
                          <p className={`mt-1 text-sm font-black ${selectedDayProductionTotals.pending > 0 ? 'text-amber-600' : 'text-emerald-700'}`}>
                            {fmt(selectedDayProductionTotals.pending)}u
                          </p>
                        </div>
                      </div>

                      {selectedDayProductionRows.map((row) => (
                        <div key={row.key} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-[10px] font-black uppercase tracking-wider text-slate-800 truncate">{row.name}</p>
                              <p className="text-[9px] font-bold text-slate-500 truncate">{row.siteLabel}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-lg border text-[8px] font-black uppercase tracking-widest ${
                              row.pending > 0
                                ? 'border-amber-200 bg-amber-50 text-amber-700'
                                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            }`}>
                              {row.pending > 0 ? `${fmt(row.pending)}u pendientes` : 'Al dia'}
                            </span>
                          </div>

                          <div className="mt-3 grid grid-cols-3 gap-2">
                            <div className="rounded-xl bg-slate-50 px-2.5 py-2">
                              <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Enviado</p>
                              <p className="mt-1 text-[10px] font-black text-slate-700">{fmt(row.requested)}u</p>
                            </div>
                            <div className="rounded-xl bg-emerald-50 px-2.5 py-2">
                              <p className="text-[8px] font-black uppercase tracking-widest text-emerald-500">Producido</p>
                              <p className="mt-1 text-[10px] font-black text-emerald-700">{fmt(row.produced)}u</p>
                            </div>
                            <div className="rounded-xl bg-amber-50 px-2.5 py-2">
                              <p className="text-[8px] font-black uppercase tracking-widest text-amber-500">Pendiente</p>
                              <p className="mt-1 text-[10px] font-black text-amber-700">{fmt(row.pending)}u</p>
                            </div>
                          </div>

                          <p className="mt-2 text-[9px] font-bold text-slate-500">
                            {row.requestsCount} envio(s) a produccion · {row.recordsCount} registro(s) cargado(s)
                          </p>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="h-[220px] flex flex-col items-center justify-center text-center text-slate-300">
                      <Factory size={26} />
                      <p className="mt-2 text-[10px] font-black uppercase tracking-widest">Sin produccion registrada</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {activeKpiDetail ? (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/35 backdrop-blur-[1px]" onClick={() => setActiveKpi(null)}>
          <div
            className="h-full w-full max-w-xl border-l border-slate-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex h-full flex-col">
              <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-slate-50 px-6 py-5">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Detalle KPI</p>
                  <h3 className="mt-1 text-lg font-black uppercase tracking-tight text-slate-800">{activeKpiDetail.title}</h3>
                  <p className="mt-1 text-[11px] font-bold text-slate-500">{activeKpiDetail.subtitle}</p>
                </div>

                <button
                  type="button"
                  onClick={() => setActiveKpi(null)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 transition-all hover:border-[#51A7FD] hover:text-[#0B2A36]"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5 custom-scrollbar">
                {activeKpiDetail.items.length > 0 ? (
                  <div className="space-y-3">
                    {activeKpiDetail.items.map((item) => (
                      <div key={item.id} className="rounded-[24px] border border-slate-200 bg-[linear-gradient(135deg,_#ffffff_0%,_#f8fbff_100%)] p-4 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[11px] font-black uppercase tracking-wider text-slate-800 truncate">{item.title}</p>
                            {item.subtitle ? <p className="mt-1 text-[10px] font-bold text-slate-500">{item.subtitle}</p> : null}
                          </div>

                          {item.badge ? (
                            <span className="shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1 text-[9px] font-black uppercase tracking-widest text-slate-700">
                              {item.badge}
                            </span>
                          ) : null}
                        </div>

                        {item.meta ? (
                          <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-2 text-[10px] font-bold text-slate-500">
                            {item.meta}
                          </div>
                        ) : null}

                        {item.chips?.length ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {item.chips.map((chip) => (
                              <span key={chip.key} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold text-slate-600">
                                {chip.label}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex h-full min-h-[240px] flex-col items-center justify-center text-center text-slate-300">
                    <CalendarDays size={30} />
                    <p className="mt-3 text-[10px] font-black uppercase tracking-widest">{activeKpiDetail.empty}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
